import React from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

// What the campaign owes, what it has already paid, and what is left to pay it with.
//
// There is no budget cap, so the first three figures are the whole of the spend question. A card's
// outcome is decided when it is issued, so the money is promised from that moment: won is the part
// already credited, unwon is the part still sitting on cards nobody has played, and the two always
// add back up to promised.
//
// The last two answer the opposite question. Every sale puts money into two pockets: most of it
// into the one the small prizes come out of, and a share into the one saving for the big prize,
// which pays nothing until it covers that prize in full. Those two are campaign wide and do not
// move when the area or dealer filter changes. Every number is calculated by the backend.

const money = (value) =>
     new Intl.NumberFormat("en-GB", {
          style: "currency",
          currency: "GBP",
          maximumFractionDigits: 2,
     }).format(value || 0);

const TILES = [
     {
          key: "promisedGbp",
          countKey: "promisedCards",
          label: "Promised",
          hint: "Every winning card, played or not",
     },
     {
          key: "wonGbp",
          countKey: "wonCards",
          label: "Won",
          hint: "Scratched, points credited",
     },
     {
          key: "unwonGbp",
          countKey: "unwonCards",
          label: "Unwon",
          hint: "Issued and still unplayed",
     },
];

export default function OptimumFrictionReportSummary(props) {
     const { report } = props;

     const heroPot = report?.heroPotGbp || 0;
     const heroPrize = report?.heroPrizeGbp || 0;
     const heroReady = heroPrize > 0 && heroPot >= heroPrize;

     return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
               {TILES.map((tile) => (
                    <Paper
                         key={tile.key}
                         variant="outlined"
                         sx={{ px: 3, py: 2, minWidth: 200 }}
                    >
                         <Typography variant="caption" color="text.secondary">
                              {tile.label}
                         </Typography>
                         <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                              {money(report?.[tile.key])}
                         </Typography>
                         <Typography variant="caption" color="text.secondary">
                              {report?.[tile.countKey] ?? 0} cards &middot;{" "}
                              {tile.hint}
                         </Typography>
                    </Paper>
               ))}

               <Paper
                    variant="outlined"
                    sx={{ px: 3, py: 2, minWidth: 200 }}
               >
                    <Typography variant="caption" color="text.secondary">
                         Small prize pot
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                         {money(report?.standardPotGbp)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                         Left to pay the everyday prizes
                    </Typography>
               </Paper>

               <Paper
                    variant="outlined"
                    sx={{ px: 3, py: 2, minWidth: 200 }}
               >
                    <Typography variant="caption" color="text.secondary">
                         Big prize pot
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                         {money(heroPot)} of {money(heroPrize)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                         {heroReady
                              ? "Full, the next round can carry it"
                              : "Still building"}
                         {" \u00b7 "}
                         {report?.heroPrizesWon ?? 0} of{" "}
                         {report?.heroPrizesIssued ?? 0} won
                    </Typography>
               </Paper>
          </Box>
     );
}

export { money };
